import { z } from 'zod';

/**
 * Literal text at the beginning of a paragraph, not a native Word list label.
 * Keeping the exact whitespace makes manual bullets survive a content rewrite
 * without guessing list semantics or changing the lawyer's indentation.
 */
export const CanvasTextListPrefixSchema = z.object({
  prefix: z.string().min(2).max(80),
  marker: z.enum(['-', '–', '—', '•', '◦', '▪', '▫', '●', '○', '‣', '⁃']),
  separator: z.string().min(1).max(79).regex(/^[ \t\u00a0]+$/u),
  preserveWhenRewriting: z.literal(true),
}).strict();
export type CanvasTextListPrefix = z.infer<typeof CanvasTextListPrefixSchema>;

/** Read an observed manual prefix from plain paragraph text, without decoding Markdown. */
export function readTextListPrefix(plain: string): CanvasTextListPrefix | undefined {
  // Horizontal whitespace only: a new line must never turn an inline dash into
  // a paragraph prefix, and a marker without a non-whitespace body is not useful context.
  const match = /^([ \t\u00a0]*)([-–—•◦▪▫●○‣⁃])([ \t\u00a0]+)(?=\S)/u.exec(plain);
  if (!match || match[0].length > 80 || match[3] === undefined) return undefined;
  return {
    prefix: match[0],
    marker: match[2] as CanvasTextListPrefix['marker'],
    separator: match[3],
    preserveWhenRewriting: true,
  };
}
